<template>
  <div class="white fill-height flex-grow-1 d-flex flex-column">
    <ServiceRideHeader :ride="ride" v-on="$listeners" />
    <v-tabs v-model="selectedTab" fixed-tabs class="flex-grow-0">
      <v-tab>Serwis</v-tab>
      <v-tab>Relacja</v-tab>
      <v-tab>
        <pulse-badge :isVisible="rideHasComments" offset-y="5px" offset-x="-5px">Komentarze</pulse-badge>
      </v-tab>
      <v-tab>Komunikacja</v-tab>
    </v-tabs>
    <v-divider></v-divider>
    <v-tabs-items v-model="selectedTab" class="flex-grow-1">
      <v-tab-item class="tab__item--full-height overflow-y-auto">
        <ServiceRideGeneralInfo :ride="ride" />
        <template v-if="isCourseHasReport">
          <v-divider></v-divider>
          <section class="mx-6 my-4 d-flex flex-column" style="gap: 16px">
            <BaseTypography variant="subtitle">Raport serwisu</BaseTypography>
            <section v-if="driverComment" class="d-flex flex-column" style="gap: 16px">
              <BaseTypography variant="subtitle-2">Komentarz serwisanta</BaseTypography>
              <div class="driver-comment">
                <BaseTypography variant="subtitle-3">{{ driverComment }}</BaseTypography>
              </div>
            </section>
            <section v-if="isSignedDocumentsSupplied" class="d-flex flex-column" style="gap: 16px">
              <BaseTypography variant="subtitle-2">Zdjęcia podpisanych dokumentów</BaseTypography>
              <FileStatus
                class="my-2"
                v-for="file in signedDocuments"
                :key="file.name"
                :file="file"
                isReadonly
                @click.native="downloadAttachment(file)"
              />
            </section>
            <section v-if="isDocumentsOfWorkSupplied" class="d-flex flex-column" style="gap: 16px">
              <BaseTypography variant="subtitle-2">Zdjęcia wykonanej pracy</BaseTypography>
              <FileStatus
                v-for="file in documentsOfWork"
                :key="file.name"
                :file="file"
                isReadonly
                @click.native="downloadAttachment(file)"
              />
            </section>
          </section>
        </template>
        <v-divider></v-divider>
        <section class="ma-6 d-flex flex-column" style="gap: 16px">
          <div class="d-flex align-center justify-space-between">
            <BaseTypography variant="subtitle">Usługi do wykonania</BaseTypography>
            <BaseTextButton prependIcon="mdi-plus" @click="openAddServiceItemModal">Dodaj usługę</BaseTextButton>
          </div>

          <ServiceTaskOverviewCard
            v-for="service in ride.services"
            :key="service.type"
            :service="service"
            :isRemoveOptionVisible="ride.services.length > 1"
            @edit="openEditServiceModal"
            @removeServiceItem="removeServiceItem"
          />

          <ServiceSelectionModal
            isCoursePreview
            :services="ride.services"
            :value="addServiceModal.isOpen"
            @selectServiceItem="openEditServiceModal"
            @close="closeAddServiceItemModal"
          />

          <ServiceDetailsModal
            isCoursePreview
            :isAddMode="addServiceModal.isOpen"
            :value="editServiceModal.isOpen"
            :serviceItem="editServiceModal.serviceItem"
            :company="ride.company"
            @close="closeEditServiceModal"
            @editExistingServiceItem="editServiceItem"
            @addServiceItem="addServiceItem"
            @downloadFile="downloadAttachment"
          />
        </section>
      </v-tab-item>
      <v-tab-item class="tab__item--full-height overflow-y-auto">
        <BaseTypography variant="subtitle-2" class="mx-6 mt-4 py-4">Relacja przejazdu</BaseTypography>
        <segments-timeline :ride="ride" :course="course" extended-view />
      </v-tab-item>
      <v-tab-item class="tab__item--full-height">
        <comment-section class="flex-grow-1 overflow-y-auto" :entity="{ ...ride, '@type': 'Ride' }" />
      </v-tab-item>
      <v-tab-item class="tab__item--full-height">
        <ride-message-section class="flex-grow-1 overflow-y-auto" :ride="ride" />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import BaseTypography from '@/component/Base/base-typography';
import PulseBadge from '@/component/Base/pulse-badge';
import CommentSection from '@/component/NewCourse/Show/Details/comment-section';
import RideMessageSection from '@/component/NewCourse/Show/Details/ride-message-section';
import BaseTextButton from '@/component/Base/base-text-button';
import ServiceTaskOverviewCard from '@/controller/Service/service-task-card/service-task-overview-card';
import SegmentsTimeline from '@/component/NewCourse/Segments/segments-timeline-factory';
import ServiceSelectionModal from '@/controller/Service/service-selection-modal/service-selection-modal';
import ServiceDetailsModal from '@/controller/Service/service-details-modal/service-details-modal';
import { TAXI_COURSE_RIDE_STATUS } from '@/core/dict/taxi-course-dict';
import { ATTACHMENT_TYPE_DICT } from '@/core/dict/attachment-type-dict';
import FileStatus from '@/component/Base/file-upload/file-status';
import ServiceRideHeader from './service-ride-header';
import ServiceRideGeneralInfo from './service-ride-general-info';

const RELATION_TAB = 0;

export default {
  name: 'ServiceRideDrawer',
  components: {
    ServiceRideHeader,
    PulseBadge,
    CommentSection,
    RideMessageSection,
    ServiceRideGeneralInfo,
    BaseTypography,
    SegmentsTimeline,
    BaseTextButton,
    ServiceTaskOverviewCard,
    ServiceDetailsModal,
    ServiceSelectionModal,
    FileStatus,
  },
  props: {
    ride: Object,
    course: Object,
  },
  data() {
    return {
      selectedTab: RELATION_TAB,
      editServiceModal: {
        isOpen: false,
        serviceItem: null,
      },
      addServiceModal: {
        isOpen: false,
        serviceItem: null,
      },
    };
  },
  computed: {
    rideHasComments() {
      return this.ride.comments.length > 0;
    },
    isCourseHasReport() {
      return this.course.billFromDriver !== null && this.ride.status === TAXI_COURSE_RIDE_STATUS.FINISHED;
    },
    driverComment() {
      return this.course.driverComment;
    },
    isSignedDocumentsSupplied() {
      return this.signedDocuments.length > 0;
    },
    isDocumentsOfWorkSupplied() {
      return this.documentsOfWork.length > 0;
    },
    documentsOfWork() {
      return this.ride.attachments
        .filter(({ type }) => type === ATTACHMENT_TYPE_DICT.DOCUMENT_OF_WORK)
        .map((v) => ({ ...v, name: v.mediaObject.filePath }));
    },
    signedDocuments() {
      return this.ride.attachments
        .filter(({ type }) => type === ATTACHMENT_TYPE_DICT.SIGNED_DOCUMENT)
        .map((v) => ({ ...v, name: v.mediaObject.filePath }));
    },
  },
  methods: {
    removeServiceItem(v) {
      this.$store.dispatch('service/removeServiceItemFromExistingServiceCourse', {
        course: this.course,
        ride: this.ride,
        serviceType: v.serviceType,
      });
    },
    openEditServiceModal(v) {
      this.editServiceModal.isOpen = true;
      this.editServiceModal.serviceItem = v;
    },
    closeEditServiceModal() {
      this.editServiceModal.isOpen = false;
      this.editServiceModal.serviceItem = null;
    },
    editServiceItem(service) {
      this.$store.dispatch('service/updateServiceItemFromExistingServiceCourse', {
        course: this.course,
        ride: this.ride,
        service,
      });
    },
    openAddServiceItemModal() {
      this.addServiceModal.isOpen = true;
    },
    closeAddServiceItemModal() {
      this.addServiceModal.isOpen = false;
      this.addServiceModal.serviceItem = null;
    },
    addServiceItem(v) {
      this.$store.dispatch('service/addServiceItemFromExistingServiceCourse', {
        course: this.course,
        ride: this.ride,
        service: v,
      });
    },
    downloadAttachment(attachment) {
      this.$store.dispatch('service/downloadAttachment', { attachment, courseId: this.course.id });
    },
  },
};
</script>

<style lang="scss" scoped>
.tab__item--full-height {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

::v-deep .v-window__container {
  height: 100%;
}

.driver-comment {
  padding: 16px 16px 8px;
  background: #fff5e5;
  border-radius: 8px;
}
</style>
